/**
 * The voice pool (Phase 8): up to 32 playing sources, each source -> gain -> (panner ->) bus.
 * Positional voices use an equal-power panner with inverse distance falloff; loops belong to a
 * key (a seat's jets, a beam) and fade out when nothing refreshes them for a few frames.
 */
import type { AudioCue } from '@steelwake/shared/data';

import { chooseSlot, type ActiveVoice } from './bank-logic.js';

export interface Vec3Like {
  x: number;
  y: number;
  z: number;
}

export interface Voice extends ActiveVoice {
  source: AudioBufferSourceNode;
  gain: GainNode;
  panner: PannerNode | null;
  loopKey: string | null;
  refreshedAt: number;
  ended: boolean;
}

export interface PlayOptions {
  position: Vec3Like | null;
  loopKey: string | null;
  rate: number;
  /** Linear gain for the voice (normalisation, mix and any request trim, already combined). */
  gain: number;
  loopStart: number;
  loopEnd: number;
}

/** A loop nobody refreshed for this long is over. */
const LOOP_TIMEOUT = 0.2;
const LOOP_FADE = 0.08;
const REF_DISTANCE = 6;
const MAX_DISTANCE = 120;

export class VoicePool {
  private active: Voice[] = [];

  constructor(
    private readonly ctx: AudioContext,
    readonly capacity = 32,
  ) {}

  /** Starts a voice, evicting per the bank rules; null when the cue was refused. */
  play(cue: AudioCue, buffer: AudioBuffer, bus: AudioNode, opts: PlayOptions): Voice | null {
    const now = this.ctx.currentTime;
    if (opts.loopKey !== null) {
      const existing = this.active.find((v) => v.loopKey === opts.loopKey && !v.ended);
      if (existing) {
        this.refreshLoop(opts.loopKey, opts.position, now);
        return existing;
      }
    }
    const choice = chooseSlot(this.active, this.capacity, cue.id, cue.maxInstances, cue.priority);
    if (choice.kind === 'refuse') return null;
    if (choice.kind === 'steal') this.stop(choice.victim, now, 0.02);

    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = opts.rate;
    if (cue.loop) {
      source.loop = true;
      source.loopStart = opts.loopStart;
      source.loopEnd = opts.loopEnd;
    }
    const gain = this.ctx.createGain();
    gain.gain.value = opts.gain;
    let panner: PannerNode | null = null;
    if (cue.positional && opts.position) {
      panner = this.ctx.createPanner();
      panner.panningModel = 'equalpower';
      panner.distanceModel = 'inverse';
      panner.refDistance = REF_DISTANCE;
      panner.maxDistance = MAX_DISTANCE;
      panner.rolloffFactor = 1;
      setPannerPosition(panner, opts.position, now);
      source.connect(gain).connect(panner).connect(bus);
    } else {
      source.connect(gain).connect(bus);
    }
    const voice: Voice = {
      cueId: cue.id,
      priority: cue.priority,
      startedAt: now,
      source,
      gain,
      panner,
      loopKey: opts.loopKey,
      refreshedAt: now,
      ended: false,
    };
    source.onended = () => {
      voice.ended = true;
    };
    source.start(now);
    this.active.push(voice);
    return voice;
  }

  /** Keeps a loop alive and moves it with its source. */
  refreshLoop(key: string, position: Vec3Like | null, now: number): void {
    for (const v of this.active) {
      if (v.loopKey !== key || v.ended) continue;
      v.refreshedAt = now;
      if (v.panner && position) setPannerPosition(v.panner, position, now);
    }
  }

  stopLoop(key: string, now: number): void {
    for (const v of this.active) if (v.loopKey === key && !v.ended) this.stop(v, now, LOOP_FADE);
  }

  /** Drops finished voices and ends loops that stopped being refreshed. */
  update(now: number): void {
    for (const v of this.active) {
      if (v.loopKey !== null && !v.ended && now - v.refreshedAt > LOOP_TIMEOUT) {
        this.stop(v, now, LOOP_FADE);
      }
    }
    this.active = this.active.filter((v) => !v.ended);
  }

  stopAll(): void {
    const now = this.ctx.currentTime;
    for (const v of this.active) this.stop(v, now, 0.05);
    this.active = [];
  }

  count(): number {
    return this.active.filter((v) => !v.ended).length;
  }

  loops(): number {
    return this.active.filter((v) => v.loopKey !== null && !v.ended).length;
  }

  private stop(v: Voice, now: number, fade: number): void {
    if (v.ended) return;
    v.ended = true;
    v.loopKey = null;
    try {
      v.gain.gain.setTargetAtTime(0, now, fade / 3);
      v.source.stop(now + fade);
    } catch {
      // Already stopped (a source that ended between the check and the call).
    }
  }
}

function setPannerPosition(panner: PannerNode, p: Vec3Like, now: number): void {
  // The AudioParam form is smooth and current; older engines only have setPosition.
  if ('positionX' in panner && panner.positionX instanceof AudioParam) {
    panner.positionX.setTargetAtTime(p.x, now, 0.02);
    panner.positionY.setTargetAtTime(p.y, now, 0.02);
    panner.positionZ.setTargetAtTime(p.z, now, 0.02);
  } else {
    panner.setPosition(p.x, p.y, p.z);
  }
}
